import { Injectable } from '@angular/core';
import { BaseService } from '../base.service';
import { HttpClient } from '@angular/common/http';
import { ProductProvider, Lottery, ProductTypeList, LogPaperUse, NotificationModel } from '../../models/lottery/lotteries.models';
import { ResponseModel } from '../../models/base-response.model';
import { forkJoin, map, Observable } from 'rxjs';
import { RafflesService } from './raffles.service';
import { LotteryInfo } from '../../models/lottery-ticket/lottery-ticket.model';
import { ContractInfo, ServiceModel, ServiceSale } from '../../models/electronic-transactions/electronic-transactions.model';
import { environment } from '../../../environments/environment';
import { ProductProviderCategory, ProductType } from '../../enums/base.enums';

@Injectable({
  providedIn: 'root'
})
export class LotteryService extends BaseService<Lottery> {

  lotterySuperPale!: LotteryInfo;

  constructor(
    private _http: HttpClient,
    private _rafflesService :RafflesService,
  ) {
    super(_http, 'lottery/banks/:bankId');
  }

  getAllLotteries(): Observable<ResponseModel<Lottery[]>> {
    return this._http.get<ResponseModel<Lottery[]>>(`${this.baseUrl}/lotteries`);
  }

  getActiveLotteries(): Observable<LotteryInfo[]> {
    return forkJoin({
      raffles: this._rafflesService.getAllOpenRaffles(),
      lotteries: this.getAllLotteries()
    }).pipe(
      map(({ raffles, lotteries }) => {
        let autoIncrementId = 0;
        const lotteryOptions:LotteryInfo[] = [];
        const activeRaffleLotteryIds = new Set(raffles.data.map(raffle => raffle.lotteryId));

        // Luego, filtra las loterías y solo inserta las que tengan una rifa activa
        lotteries.data.forEach(element => {
          if (activeRaffleLotteryIds.has(element.id)) {
            const raffle:any = raffles.data.find(r => r.lotteryId === element.id);

            if (raffle) {
              // Usa la hora (HH:mm:ss) y combínala con la fecha actual
              const [hours, minutes, seconds] = raffle.close.split(':').map(Number);
              const currentDateTime = new Date(); // Fecha actual
              const raffleTime = new Date(
                currentDateTime.getFullYear(),
                currentDateTime.getMonth(),
                currentDateTime.getDate(),
                hours,
                minutes,
                seconds
              );

              if(element.isMultiLottery == false){
                lotteryOptions.push({
                  id: autoIncrementId++,
                  realId:element.id,
                  raffleId:raffle.id,
                  name: element.name,
                  ticketName: element.ticketName,
                  time: raffleTime, // Asigna la fecha actual con la hora de la rifa
                  lotteryBets:element.lotteryBets,
                  shortcut:element.shortcut,
                  urlLogo: element.urlLogo ?? undefined
                });
              }else{
                this.lotterySuperPale = {
                  id: autoIncrementId++,
                  realId:element.id,
                  raffleId:raffle.id,
                  name: `(${element.ticketName}) ${element.name}`,
                  ticketName: element.ticketName,
                  time: raffleTime, // Asigna la fecha actual con la hora de la rifa
                  lotteryBets:element.lotteryBets,
                  urlLogo: element.urlLogo ?? undefined
                }
              }
            }
          }
        });

        return lotteryOptions;
      })
    );
  }

  getProductsByType(
    productType: ProductType | null,
    category: ProductProviderCategory | null
  ): Observable<ResponseModel<ServiceModel[]>> {
    // Crear parámetros de la consulta
    const params: { [key: string]: string } = {};

    if (productType !== null && productType !== undefined) {
      params['productType'] = productType.toString();
    }

    if (category !== null && category !== undefined) {
      params['category'] = category.toString();
    }

    // Construir la URL con los parámetros dinámicos
    const queryString = new URLSearchParams(params).toString();
    const url = `${this.baseUrl}/products${queryString ? '?' + queryString : ''}`;

    return this._http.get<ResponseModel<ServiceModel[]>>(url);
  }

  getContractInfo(productId:string,contract:string): Observable<ResponseModel<ContractInfo[]>> {
    return this._http.get<ResponseModel<ContractInfo[]>>(`${environment.apiUrl}/lottery/contracts/info?productId=${productId}&nic=${contract}`);
  }

  postService(data:ServiceSale): Observable<ResponseModel<any>> {
    return this._http.post<ResponseModel<any>>(`${environment.apiUrl}/lottery/contracts/sales`,data);
  }

  getProductProviders(category:Number): Observable<ResponseModel<ProductProvider[]>> {
    return this._http.get<ResponseModel<ProductProvider[]>>(`${this.baseUrl}/product-providers?category=${category}`);
  }

  getProductByProvider(providerId:string): Observable<ResponseModel<ProductTypeList[]>> {
    return this._http.get<ResponseModel<ProductTypeList[]>>(`${this.baseUrl}/products?providerId=${providerId}`);
  }

  getBankDetail(): Observable<ResponseModel<any>> {
    return this._http.get<ResponseModel<any>>(`${this.baseUrl}/details`);
  }

  logPaperUse(data: LogPaperUse): Observable<ResponseModel<any>> {
    const url = `${environment.apiUrl}/lottery/log-prints`;
    return this._http.post<ResponseModel<any>>(url, data);
  }

  getNotifications(): Observable<ResponseModel<NotificationModel[]>> {
    const terminalId = localStorage.getItem("TId") ?? ''
    return this._http.get<ResponseModel<NotificationModel[]>>(`${environment.apiUrl}/lottery/notification-by-platforms/${terminalId}/reads`);
  }
}
